/**
 * Client-Side Caching Service using IndexedDB
 * Provides persistent, intelligent caching for assets, bookings, and user data
 */

class ClientCacheService {
  constructor() {
    this.dbName = 'BookingCalendarCache';
    this.version = 1;
    this.db = null;
    this.isInitialized = false;
  }

  async init() {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Assets store
        if (!db.objectStoreNames.contains('assets')) {
          const assetsStore = db.createObjectStore('assets', { keyPath: '_id' });
          assetsStore.createIndex('type', 'type');
          assetsStore.createIndex('available', 'available');
        }

        // Bookings store with compound indexes for fast filtering
        if (!db.objectStoreNames.contains('bookings')) {
          const bookingsStore = db.createObjectStore('bookings', { keyPath: 'id' });
          bookingsStore.createIndex('assetId', 'assetId');
          bookingsStore.createIndex('userId', 'userId');
          bookingsStore.createIndex('start', 'start');
          bookingsStore.createIndex('end', 'end');
          bookingsStore.createIndex('assetStart', ['assetId', 'start']);
        }

        // User cache store
        if (!db.objectStoreNames.contains('users')) {
          db.createObjectStore('users', { keyPath: 'id' });
        }

        // Metadata store for cache invalidation
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
      };
    });
  }

  // Helper to await an IDBRequest
  _awaitRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Smart caching with TTL and version checking
  async cacheAssets(assets, serverTimestamp) {
    await this.init();
    const transaction = this.db.transaction(['assets', 'metadata'], 'readwrite');
    
    try {
      // Clear old assets
      await transaction.objectStore('assets').clear();
      
      // Store new assets
      for (const asset of assets) {
        await transaction.objectStore('assets').put({
          ...asset,
          cachedAt: Date.now(),
          serverTimestamp
        });
      }

      // Update metadata
      await transaction.objectStore('metadata').put({
        key: 'assetsLastUpdate',
        value: serverTimestamp,
        cachedAt: Date.now()
      });

      console.log(`📦 Cached ${assets.length} assets in IndexedDB`);
    } catch (error) {
      console.error('Error caching assets:', error);
    }
  }

  async getCachedAssets(maxAge = 5 * 60 * 1000) { // 5 minutes default
    await this.init();
    
    try {
      const transaction = this.db.transaction(['assets', 'metadata'], 'readonly');
      const metadata = await transaction.objectStore('metadata').get('assetsLastUpdate');
      
      if (!metadata || (Date.now() - metadata.cachedAt) > maxAge) {
        return null; // Cache expired
      }

  // Use getAll() instead of a cursor loop to avoid cursor.continue() issues
  const assetsStore = transaction.objectStore('assets');
  const allAssets = await this._awaitRequest(assetsStore.getAll());

  const assets = Array.isArray(allAssets) ? allAssets : [];

  console.log(`📦 Retrieved ${assets.length} assets from IndexedDB cache`);
  // Treat empty cached arrays as expired so caller falls back to server
  if (assets.length === 0) return null;
  return assets;
    } catch (error) {
      console.error('Error retrieving cached assets:', error);
      return null;
    }
  }

  // Intelligent booking caching with conflict-aware indexing
  async cacheBookings(bookingsData, serverTimestamp = Date.now(), scope = 'future') {
    await this.init();
    const transaction = this.db.transaction(['bookings', 'metadata'], 'readwrite');
    
    try {
      // Clear old bookings
      await transaction.objectStore('bookings').clear();
      
      // Flatten and store bookings with equipment references
      const flatBookings = [];
      Object.values(bookingsData).forEach(bookings => {
        bookings.forEach(booking => {
          flatBookings.push({
            ...booking,
            cachedAt: Date.now(),
            serverTimestamp
          });
        });
      });

      for (const booking of flatBookings) {
        await transaction.objectStore('bookings').put(booking);
      }

      // Update metadata
      await transaction.objectStore('metadata').put({
        key: 'bookingsLastUpdate',
        value: serverTimestamp,
        cachedAt: Date.now(),
        scope
      });

      console.log(`📅 Cached ${flatBookings.length} bookings in IndexedDB`);
    } catch (error) {
      console.error('Error caching bookings:', error);
    }
  }

  // Fast booking retrieval with client-side filtering
  async getCachedBookings(assetIds = null, maxAge = 2 * 60 * 1000, requiredScope = 'future') { // 2 minutes for bookings
    await this.init();
    
    try {
      const transaction = this.db.transaction(['bookings', 'metadata'], 'readonly');
      const metadata = await transaction.objectStore('metadata').get('bookingsLastUpdate');
      
      if (!metadata || (Date.now() - metadata.cachedAt) > maxAge) {
        return null; // Cache expired
      }
      if (metadata.scope !== requiredScope) {
        return null; // Cache contents don't match the requested scope
      }

      const bookingsStore = transaction.objectStore('bookings');
      const result = {};
      // Use getAll() to fetch bookings and then group them in memory
      const allBookings = await this._awaitRequest(bookingsStore.getAll());
      if (Array.isArray(allBookings)) {
        for (const booking of allBookings) {
          ['wingId', 'foilId', 'boardId'].forEach(type => {
            // Support bookings where asset IDs may be top-level or inside extendedProps
            const assetRef = booking && (booking[type] || (booking.extendedProps && booking.extendedProps[type]));
            if (assetRef) {
              const assetIdStr = assetRef.toString();
              if (!result[assetIdStr]) result[assetIdStr] = [];
              result[assetIdStr].push(booking);
            }
          });
        }
      }

  const count = Object.keys(result).length;
  console.log(`📅 Retrieved bookings for ${count} assets from IndexedDB cache`);
  // If no bookings were found in cache, return null to force server fetch
  if (count === 0) return null;
  return result;
    } catch (error) {
      console.error('Error retrieving cached bookings:', error);
      return null;
    }
  }

  // Client-side conflict detection (runs locally, no server call needed)
  async checkConflictsLocally(assetId, startTime, endTime, excludeBookingId = null) {
    await this.init();
    
    try {
      const transaction = this.db.transaction(['bookings'], 'readonly');
      const index = transaction.objectStore('bookings').index('assetId');
      const conflicts = [];
      // Use index.getAll(key) to get all bookings for this asset
      const bookingsForAsset = await this._awaitRequest(index.getAll(assetId));
      if (Array.isArray(bookingsForAsset)) {
        for (const booking of bookingsForAsset) {
          if (excludeBookingId && booking.id === excludeBookingId) continue;
          const bookingStart = new Date(booking.start);
          const bookingEnd = new Date(booking.end);
          if (bookingStart < endTime && bookingEnd > startTime) conflicts.push(booking);
        }
      }

      return conflicts;
    } catch (error) {
      console.error('Error checking conflicts locally:', error);
      return [];
    }
  }

  // Cache user data for avatar persistence
  async cacheUserData(users) {
    await this.init();
    const transaction = this.db.transaction(['users'], 'readwrite');
    
    try {
      for (const user of users) {
        await transaction.objectStore('users').put({
          ...user,
          cachedAt: Date.now()
        });
      }
    } catch (error) {
      console.error('Error caching user data:', error);
    }
  }

  async getCachedUserData(userId) {
    await this.init();
    
    try {
      const transaction = this.db.transaction(['users'], 'readonly');
      const user = await transaction.objectStore('users').get(userId);
      
      if (user && (Date.now() - user.cachedAt) < 10 * 60 * 1000) { // 10 minutes
        return user;
      }
      
      return null;
    } catch (error) {
      console.error('Error retrieving cached user data:', error);
      return null;
    }
  }

  // Remove orphaned bookings from cache (bookings that no longer exist on server)
  async removeOrphanedBookings(serverBookingsData) {
    await this.init();
    
    try {
      const transaction = this.db.transaction(['bookings'], 'readwrite');
      const bookingsStore = transaction.objectStore('bookings');
      
      // Get all cached booking IDs
      const cachedBookingIds = new Set();
      const allCached = await this._awaitRequest(bookingsStore.getAll());
      if (Array.isArray(allCached)) {
        for (const b of allCached) cachedBookingIds.add(b.id);
      }
      
      // Get all server booking IDs
      const serverBookingIds = new Set();
      Object.values(serverBookingsData).forEach(bookings => {
        bookings.forEach(booking => {
          serverBookingIds.add(booking.id);
        });
      });
      
      // Remove orphaned bookings
      let removedCount = 0;
      for (const cachedId of cachedBookingIds) {
        if (!serverBookingIds.has(cachedId)) {
          await bookingsStore.delete(cachedId);
          removedCount++;
        }
      }
      
      if (removedCount > 0) {
        console.log(`🧹 Removed ${removedCount} orphaned bookings from cache`);
      }
      
      return removedCount;
    } catch (error) {
      console.error('Error removing orphaned bookings:', error);
      return 0;
    }
  }

  // Clear cache when needed
  async clearCache() {
    await this.init();
    const transaction = this.db.transaction(['assets', 'bookings', 'users', 'metadata'], 'readwrite');
    
    try {
      await Promise.all([
        transaction.objectStore('assets').clear(),
        transaction.objectStore('bookings').clear(),
        transaction.objectStore('users').clear(),
        transaction.objectStore('metadata').clear()
      ]);
      
      console.log('🧹 Client cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Get cache statistics
  async getCacheStats() {
    await this.init();
    
    try {
      const transaction = this.db.transaction(['assets', 'bookings', 'users'], 'readonly');
      
      const assetsCount = await transaction.objectStore('assets').count();
      const bookingsCount = await transaction.objectStore('bookings').count();
      const usersCount = await transaction.objectStore('users').count();
      
      return {
        assets: assetsCount,
        bookings: bookingsCount,
        users: usersCount
      };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return { assets: 0, bookings: 0, users: 0 };
    }
  }
}

// Export singleton
export const clientCache = new ClientCacheService();
